import React, { useState } from 'react';
import { TextField, Button, Container, Box, Typography, Card, Alert, Collapse } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { AccountService } from "../../service/accountService";

const Onboarding = () => {
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const validateForm = () => {
        const newErrors: string[] = [];
        
        if (!identifier.trim()) {
            newErrors.push('Bitte geben Sie Ihren Waschstraßennamen oder Ihre E-Mail-Adresse ein');
        }
        
        if (!password) {
            newErrors.push('Bitte geben Sie Ihr Passwort ein');
        }
        
        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleLogin = async () => {
        try {
            // Validierung durchführen
            if (!validateForm()) {
                return;
            }
            
            setIsLoading(true);
            setErrors([]); // Fehler zurücksetzen
            
            const loginRequest = {
                email: identifier,
                password: password,
            };

            console.log('[DEBUG] Login-Anfrage senden von UI');
            const session = await AccountService.login(loginRequest);
            console.log('[DEBUG] Login-Antwort in UI erhalten:', session);

            if (session && session.loggedIn) {
                console.log('[DEBUG] Login erfolgreich, navigiere zu /dashboard');
                navigate('/dashboard');
            } else {
                console.error('[DEBUG] Login fehlgeschlagen, session:', session);
                setErrors(['Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.']);
            }
        } catch (error: any) {
            console.error('[DEBUG] Error beim Login:', error);
            
            // Spezifische Fehlermeldungen basierend auf dem Fehler
            if (error.message && error.message.includes('401')) {
                setErrors(['Benutzername oder Passwort ist falsch']);
            } else if (error.message && error.message.includes('404')) {
                setErrors(['Benutzer nicht gefunden. Bitte überprüfen Sie Ihre Eingaben.']);
            } else if (error.errorMessages && Array.isArray(error.errorMessages)) {
                setErrors(error.errorMessages);
            } else {
                setErrors(['Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.']);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Funktion, um Eingabetaste zu behandeln
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleLogin();
        }
    };

    // Neue Funktion zum Navigieren zum Kontaktformular
    const handleContactClick = () => {
        // Diese Methode navigiert zur Landing-Page und setzt das Hash-Fragment
        navigate('/#kontakt');
    };

    return (
        <Container maxWidth='sm'>
            <Card 
                sx={{
                    padding: '50px',
                    marginTop: '10vh',
                    marginBottom: '10vh',
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Typography 
                        component={Link} 
                        to="/"
                        sx={{ 
                            textAlign: 'center', 
                            fontFamily: 'Inter, sans-serif', 
                            margin: 2,
                            textDecoration: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8
                            }
                        }} 
                        variant="h3"
                    >
                        WashProTech
                    </Typography>
                    
                    <Collapse in={errors.length > 0} sx={{ width: '100%', mb: 2 }}>
                        {errors.map((error, index) => (
                            <Alert 
                                key={index} 
                                severity="error" 
                                sx={{ width: '100%', mb: 1 }}
                                onClose={() => setErrors(errors.filter((_, i) => i !== index))}
                            >
                                {error}
                            </Alert>
                        ))}
                    </Collapse>
                    
                    <TextField
                        fullWidth
                        label="Waschstraßenname oder E-Mail"
                        variant="outlined"
                        margin="normal"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        onKeyDown={handleKeyDown}
                        error={errors.some(error => error.includes('Waschstraßennamen') || error.includes('E-Mail'))}
                        disabled={isLoading}
                    />
                    <TextField
                        fullWidth
                        type="password"
                        label="Passwort"
                        variant="outlined"
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                        error={errors.some(error => error.includes('Passwort'))}
                        disabled={isLoading}
                    />
                    <Button
                        className="MuiButton-primary"
                        fullWidth
                        style={{ marginTop: '10px' }}
                        onClick={handleLogin}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Anmeldung...' : 'Login'}
                    </Button>

                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2, mb: 1 }}
                    >
                        Die Registrierung ist während der Testphase deaktiviert.
                        Bitte kontaktieren Sie den Administrator für einen Testzugang.
                    </Typography>
                    
                    <Button
                        color="primary"
                        variant="text"
                        sx={{ mt: 2 }}
                        onClick={handleContactClick}
                    >
                        Zum Kontaktformular
                    </Button>
                </Box>
            </Card>
        </Container>
    );
};

export default Onboarding;
